import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getSymbolData } from '../utils/sixyaoData';
import { useLanguage } from '../context/LanguageContext';

// ----------------------------------------------------
// AncientCoin — CSS-rendered 方孔圆钱 with 3-D flip
// isYang=true  → "字面" (yang/heads), gold face up
// isYang=false → "背面" (yin/tails),  dark face up
// ----------------------------------------------------
const AncientCoin = ({ isYang, delay, isResultReady }) => {
    const [animClass, setAnimClass] = useState('');

    useEffect(() => {
        if (isResultReady) {
            setAnimClass('');
            const t = setTimeout(() => {
                setAnimClass(isYang ? 'spin-to-yang' : 'spin-to-yin');
            }, delay);
            return () => clearTimeout(t);
        } else {
            setAnimClass('');
        }
    }, [isResultReady, isYang, delay]);

    return (
        <div className="coin-perspective">
            <div className={`ancient-coin ${animClass}`}>
                {/* Yang face — bright gold with square hole + inscription */}
                <div className="coin-face coin-yang">
                    <div className="coin-hole" />
                    <span className="coin-char">正</span>
                </div>
                {/* Yin face — dark bronze, back side */}
                <div className="coin-face coin-yin">
                    <div className="coin-hole" />
                </div>
            </div>
        </div>
    );
};


// ----------------------------------------------------
// SixYaoCoinAnimation
// ----------------------------------------------------
const SixYaoCoinAnimation = ({
    yaoResults = [],
    isRolling,
    currentHexagramName = '',
    onAnimationComplete,
}) => {
    const { t } = useLanguage();

    // Yao position names from locale (fallback to Chinese)
    const yaoPositions = useMemo(
        () => t('yaoPositions') || ['初爻', '二爻', '三爻', '四爻', '五爻', '上爻'],
        [t]
    );

    // Yao type names from locale (e.g. '老阴', 'Old Yin')
    const yaoTypeNames = useMemo(
        () => t('yaoTypeNames') || { 6: '老阴', 7: '少阳', 8: '少阴', 9: '老阳' },
        [t]
    );

    const [animatedYaoCount, setAnimatedYaoCount] = useState(0);
    const [isResultReady,    setIsResultReady]    = useState(false);
    const [currentCoinResult, setCurrentCoinResult] = useState(null);
    const [currentYaoValue,   setCurrentYaoValue]   = useState(null);
    // Show the result label only AFTER all coins have landed (~1750ms after ready)
    const [showResultLabel, setShowResultLabel] = useState(false);

    useEffect(() => {
        const totalYaos = 6;
        let revealTimer = null;

        // A. All 6 animations done → trigger callback
        if (yaoResults.length === totalYaos && animatedYaoCount === totalYaos) {
            const completionDelay = 1000;
            const timer = setTimeout(() => { onAnimationComplete(); }, completionDelay);
            return () => clearTimeout(timer);
        }

        // B. Next yao available — run coin flip for it
        if (yaoResults.length > animatedYaoCount) {
            setIsResultReady(false);
            setCurrentCoinResult(null);
            setCurrentYaoValue(null);

            const result = yaoResults[animatedYaoCount];
            setCurrentCoinResult(getSymbolData(result));
            setCurrentYaoValue(result);
            setIsResultReady(true);

            // Coin flip takes ~1.4 s (1200 ms animation + 200 ms last-coin delay)
            // Then wait an extra 1800 ms so the user can see the result
            const FLIP_DURATION  = 1200;
            const MAX_COIN_DELAY = 600;
            const EXTRA_WAIT     = 1800;

            revealTimer = setTimeout(() => {
                setAnimatedYaoCount(prev => prev + 1);
                setCurrentCoinResult(null);
                setCurrentYaoValue(null);
                setIsResultReady(false);
            }, FLIP_DURATION + MAX_COIN_DELAY + EXTRA_WAIT);
        }

        return () => { clearTimeout(revealTimer); };
    }, [yaoResults, animatedYaoCount, onAnimationComplete]);

    // Delay result label until after the last coin animation finishes
    // Last coin: delay=500ms + animation=1200ms = 1700ms → show label at 1800ms
    useEffect(() => {
        if (isResultReady) {
            setShowResultLabel(false);
            const timer = setTimeout(() => setShowResultLabel(true), 1800);
            return () => clearTimeout(timer);
        } else {
            setShowResultLabel(false);
        }
    }, [isResultReady]);

    // Reset when cleared
    useEffect(() => {
        if (!isRolling && yaoResults.length === 0) {
            setAnimatedYaoCount(0);
            setIsResultReady(false);
            setCurrentCoinResult(null);
            setCurrentYaoValue(null);
            setShowResultLabel(false);
        }
    }, [isRolling, yaoResults]);

    // ---- Top panel: current yao coin flip ----
    const renderCoinPanel = () => {
        if (!isRolling && yaoResults.length === 0) {
            return (
                <div className="coin-animation-panel ready-to-start">
                    <p className="placeholder-text">{t('hexagramDrawing')}…</p>
                </div>
            );
        }

        const currentYaoName = yaoPositions[animatedYaoCount] || t('hexagramComplete');

        if (currentCoinResult) {
            const coinYangFlags = currentCoinResult.coins.map(c => c === '🌕');
            // Localized type name (e.g. "Old Yin" / "老阴")
            const localizedTypeName = currentYaoValue != null
                ? (yaoTypeNames[currentYaoValue] || currentCoinResult.name)
                : currentCoinResult.name;

            return (
                <div className="coin-animation-panel is-result-ready">
                    <h3 className="coin-yao-name">{currentYaoName}</h3>
                    <div className="coin-result-row">
                        {coinYangFlags.map((isYang, idx) => (
                            <AncientCoin
                                key={idx}
                                isYang={isYang}
                                delay={idx * 250}
                                isResultReady={isResultReady}
                            />
                        ))}
                    </div>
                    {showResultLabel && (
                        <p className="coin-result-name">{localizedTypeName}</p>
                    )}
                </div>
            );
        }

        if (animatedYaoCount === 6) {
            return (
                <div className="coin-animation-panel complete">
                    <h3>{t('hexagramComplete')}</h3>
                    <p>{t('mainHexagramLabel')}：{currentHexagramName || t('calculating')}</p>
                </div>
            );
        }
    };

    // ---- Bottom: fixed yao lines already revealed ----
    const renderFixedYao = (coinCount, index) => {
        const { isMoving, isYang } = getSymbolData(coinCount);
        const positionName = yaoPositions[index];
        const yaoTypeClass = isYang ? 'yang' : 'yin';
        return (
            <div key={index} className={`yao-line ${yaoTypeClass} ${isMoving ? 'is-moving' : ''}`}>
                <span className="yao-position-name">{positionName}</span>
                <div className="yao-line-main" />
            </div>
        );
    };

    const renderYaoList = () => {
        const items = [];
        for (let i = 0; i < animatedYaoCount; i++) {
            items.push(renderFixedYao(yaoResults[i], i));
        }
        return items.slice().reverse();
    };

    return (
        <div className="sixyao-animation-wrapper">
            <div className="top-coin-panel">
                {renderCoinPanel()}
            </div>
            <div className="hexagram-column">
                <p className="yao-diagram-title">{t('hexagramDrawing')}…</p>
                <div className="yao-list-container">
                    {renderYaoList()}
                </div>
            </div>
        </div>
    );
};

export default SixYaoCoinAnimation;
